import {isLeft} from 'fp-ts/lib/Either'
import * as t from 'io-ts'
import reporter from 'io-ts-reporters'
import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import * as LegacyStyles from './legacy-styles'
import * as Styles from './styles'

const formatJSON = (value: unknown): string => {
  try {
    return JSON.stringify(
      typeof value === 'object' ? value : JSON.parse(String(value)),
      null,
      2
    )
  } catch {
    return value as string
  }
}

interface BaseJSONFieldProps {
  label?: ReactNode
  style?: React.CSSProperties
  wrapperStyle?: React.CSSProperties
  setValid?: (valid: boolean) => void
  tabIndex?: number
  readOnly?: boolean
  legacyMode?: boolean
  allowEmpty?: boolean
}

interface StringJSONFieldProps extends BaseJSONFieldProps {
  value: unknown
  onChange: (value: string) => void
  raw?: false
}
interface RawJSONFieldProps<A> extends BaseJSONFieldProps {
  value: unknown
  onChange: (value: A) => void
  raw: true
  codec?: t.Type<A>
}
type JSONFieldProps<A = unknown> = StringJSONFieldProps | RawJSONFieldProps<A>

export const JSONField = <A extends {}>(props: JSONFieldProps<A>) => {
  const textAreaRef = useRef(null)
  const [value, setValue] = useState<string>(formatJSON(props.value))
  const [validLocal, setValidLocal] = useState<boolean>(true)
  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const {
    setValid: setValidProp,
    value: valueProp,
    raw: rawProp,
    onChange: onChangeProp,
    wrapperStyle
  } = props

  const codecProp = 'codec' in props ? props.codec : undefined

  const setValid = useCallback(
    (valid: boolean) => {
      setValidLocal(valid)
      if (setValidProp) {
        setValidProp(valid)
      }
    },
    [setValidProp]
  )

  // update when value prop changes
  useEffect(() => {
    // Don't format json if the textarea is currently focused / in use
    if (document.activeElement !== textAreaRef.current) {
      // We use a ref above as having an isFocused in state causes us to call this on focus change
      // as well as valueProp change, in that case valueProp can be out of sync with local value
      setValue(formatJSON(valueProp))
    }
  }, [valueProp])

  const validate = useCallback(
    (newValue: string) => {
      setValue(newValue)

      if (props.allowEmpty && newValue === '') {
        setValid(true)
        return undefined
      }
      let parsed
      try {
        parsed = JSON.parse(newValue)
      } catch (e) {
        setValid(false)
        setErrorMessages(['Invalid JSON'])
        return
      }

      if (rawProp && codecProp) {
        const result = codecProp.decode(parsed)

        if (isLeft(result)) {
          setValid(false)
          setErrorMessages(reporter.report(result))
          return
        } else {
          setValid(true)
          return result.right
        }
      }

      setValid(true)
      return rawProp ? parsed : newValue
    },
    [rawProp, codecProp, setValid, props.allowEmpty]
  )

  // initial validation on mount and update
  useEffect(() => {
    validate(value)
  }, [value, validate])

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const validated = validate(e.target.value)
      if (validated || props.allowEmpty) {
        onChangeProp(validated)
      }
    },
    [onChangeProp, validate, props.allowEmpty]
  )

  const Wrapper = props.legacyMode ? LegacyStyles.Wrapper : Styles.Wrapper
  const Textarea = props.legacyMode ? LegacyStyles.Textarea : Styles.Textarea
  const Label = props.legacyMode ? LegacyStyles.Label : Styles.Label

  return (
    <Wrapper hasLabel={props.label ? true : false} style={wrapperStyle}>
      {props.label ? <Label>{props.label}</Label> : null}
      <Textarea
        disabled={props.readOnly}
        tabIndex={props.tabIndex}
        ref={textAreaRef}
        style={props.style}
        onBlur={e => {
          setValue(formatJSON(e.target.value))
        }}
        onChange={onChange}
        value={value}
        title={!validLocal ? errorMessages.join('\n') : undefined}
        valid={validLocal}
        readOnly={props.readOnly}
      />
    </Wrapper>
  )
}
